import { normalizeBookedShuttle } from "./normalizeBookedShuttle";
import { mapBookedShuttlesToSides } from "../../Lib/Utils/shuttleStops";
import { isShuttleOffer, AdmissionStatusEnum } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { OWNED_ADMISSION_STATUSES } from "./hasPendingExchange";
import { IBooking, ILegTrain } from "../../Orchestrator/Types/Index";
import { RideHailingMode } from "../../Orchestrator/Types/Interface/IRideHailing";
import { IShuttleAddedJourney } from "../../Orchestrator/Types/Interface/IShuttleBus";
import { ITrip } from "../../Orchestrator/Types/Interface/ITrip";
import { IBookedOffer } from "../../Orchestrator/Types/Interface/IOffer";

/** Admission states meaning the passenger no longer holds the travel right. */
const TERMINAL = new Set<string>([
  AdmissionStatusEnum.REFUNDED,
  AdmissionStatusEnum.RELEASED,
  AdmissionStatusEnum.EXCHANGED,
]);

/**
 * Booked shuttles for ONE leg, derived straight from the booking.
 *
 * `shuttleAddedAtom` is keyed by side alone, so on a round trip the outbound's
 * `toStation` and the inbound's `toStation` collide and hydration's merge drops
 * one — it then renders on neither row and cannot be cancelled. Re-keying that
 * atom means touching 28 references across the checkout and legacy-drawer
 * paths, which are the verified-working parts of this feature; deriving per leg
 * here fixes the surface where the collision actually bites, using the same
 * booking-as-source-of-truth pattern as [[bookedNolForLeg]].
 */
export function bookedShuttlesForLeg(
  booking: IBooking | undefined,
  trainLeg: ILegTrain | undefined,
  /** Offers THIS session refunded — see refundedShuttleOfferIdsAtom. Their
      admissions stay non-terminal until the payment session settles, so
      without this a refund followed by a re-book redisplays the old shuttle. */
  refundedOfferIds: string[] = [],
  opts: {
    /**
     * Keep only offers the passenger has PAID for, dropping PREBOOKED/ON_HOLD.
     *
     * Must be exercised HERE rather than by the caller filtering the result,
     * because `mapBookedShuttlesToSides` collapses a side to one offer with
     * last-in-array winning and says in its own comment that the caller has to
     * exclude the losers first. Filtering afterwards let an add abandoned at
     * payment — appended after the paid offer — win the side and then fail the
     * caller's ownership check, so a shuttle the passenger had paid for went
     * invisible and unrefundable while the UI offered to sell them another.
     *
     * Opt-in rather than always-on, matching `bookedNolForLeg`'s `paidOnly`.
     * Every production caller passes it today; what the default protects is the
     * documented no-admissions/non-terminal behaviour that
     * `refundedShuttleExclusion.test.ts` pins — including a PREBOOKED offer
     * surviving, which a basket-style caller would need.
     */
    ownedOnly?: boolean;
  } = {}
): Partial<Record<RideHailingMode, IShuttleAddedJourney>> {
  if (!booking || !trainLeg) return {};
  const refunded = new Set(refundedOfferIds);

  const tripsById = new Map<string, ITrip>();
  (booking.trips || []).forEach((t) => {
    if (t?.id) tripsById.set(t.id, t as unknown as ITrip);
  });

  const shuttleOffers = (booking.bookedOffers || []).filter((o) => {
    // Decided on the offer's ADMISSION product — a train offer carrying a
    // shuttle ANCILLARY_ITEM (e.g. SHTL-MBZ-REEM) is not a shuttle offer.
    // See isShuttleOffer.
    if (!isShuttleOffer(o)) return false;
    if (refunded.has((o as { offerId?: string })?.offerId || "")) return false;
    // An offer whose admissions are ALL terminal is history, not a booking.
    // No admissions at all proves nothing, so keep it.
    const admissions = o?.admissions || [];
    if (opts.ownedOnly) {
      // Deliberately stricter than the no-admissions rule below: "proves
      // nothing" cannot satisfy "is paid for", and a caller asking for owned
      // offers only is choosing to show nothing over showing an unpaid one.
      return admissions.some((a) =>
        OWNED_ADMISSION_STATUSES.has((a as { status?: string })?.status || "")
      );
    }
    if (admissions.length === 0) return true;
    return admissions.some((a) => !TERMINAL.has((a as { status?: string })?.status || ""));
  });

  const normalized = (shuttleOffers as unknown as IBookedOffer[]).map((o) =>
    normalizeBookedShuttle(o, tripsById, booking.passengers || [])
  );
  return mapBookedShuttlesToSides(
    normalized,
    trainLeg.origin?.stopPlaceRef || "",
    trainLeg.destination?.stopPlaceRef || ""
  );
}
